/**
 * Module for managing browser TTS voice and rate settings
 * Abstracts localStorage details behind a clean interface
 */

// Private localStorage key generators
const readAloudVoiceKey = (language: string) =>
  `reader.readAloudVoice.${language}`;
const readAloudRateKey = (language: string) =>
  `reader.readAloudRate.${language}`;

/**
 * Get the selected voice URI for a language
 */
export const getVoice = (language: string): string | null => {
  return localStorage.getItem(readAloudVoiceKey(language));
};

/**
 * Get the selected rate for a language (returns "1" as default if not set)
 */
export const getRate = (language: string): string => {
  return localStorage.getItem(readAloudRateKey(language)) ?? "1";
};

// Subscription mechanism for change listeners
const changeListeners = new Set<() => void>();

/**
 * Subscribe to changes in voice or rate. Returns an unsubscribe function.
 */
export const onChange = (callback: () => void): (() => void) => {
  changeListeners.add(callback);
  return () => {
    changeListeners.delete(callback);
  };
};

/**
 * Unsubscribe a previously registered change callback
 */
export const offChange = (callback: () => void): void => {
  changeListeners.delete(callback);
};

function notifyChangeListeners() {
  for (const callback of changeListeners) {
    callback();
  }
}

/**
 * Set the selected voice URI for a language
 */
export const setVoice = (language: string, voiceURI: string): void => {
  localStorage.setItem(readAloudVoiceKey(language), voiceURI);
  notifyChangeListeners();
};

/**
 * Set the selected rate for a language
 */
export const setRate = (language: string, rate: string): void => {
  localStorage.setItem(readAloudRateKey(language), rate);
  notifyChangeListeners();
};
